import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '../../../../../../../../CommonComponents/OPL/Input/Input';
import { SegmentedControl } from '../../../../../../../../CommonComponents/OPL/SegmentedControl/SegmentedControl';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { TrusteeType, TrustsDTO, TrustType } from '../../../../../../api/useTrustsQuery';
import { Checkbox } from '../../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { InputMask } from '../../../../../../../../CommonComponents/OPL/Input/InputMask';
import { GstExemptPopover } from './components/GstExemptPopover/GstExemptPopover';
import { ScrollableContent } from '../../UpsertTrustModal.styles';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { InputNumberMask } from '../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { FormValues } from '../../UpsertTrustModal.meta';

interface Props {
  isEstatePlanning?: boolean;
  data: TrustsDTO;
}

const getPersonOptionValue = (opt: { label: string; value: number }) => `${opt.value}-${opt.label}`;

export const General = ({ isEstatePlanning, data }: Props) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();

  const [trustType, trustees, grantors] = watch(['trustType', 'trustees', 'grantors']);

  // To be added later
  // const [illiquidAssetOptions] = watch(['illiquidAssetOptions']);

  /**
   * If the Trust is irrevocable and if neither the principal nor the spouse are trustees,
   * the trust is excluded from gross estate by default.
   */
  const isDefaultExcludedGrossEstate =
    trustType === TrustType.Irrevocable &&
    !trustees.find(({ value }) => value === TrusteeType.Principal || value === TrusteeType.Spouse);

  return (
    <ScrollableContent as="section" px="24" pt="24">
      <Input
        {...register('name')}
        label="Trust Name"
        formVariant={errors.name ? 'error' : undefined}
        helperText={errors.name?.message}
        mb="24"
      />

      <Controller
        control={control}
        name="trustType"
        render={({ field }) => (
          <SegmentedControl
            {...field}
            id="upsert-trust--trust-type"
            values={data.TrustTypeOptions.map(({ label }) => label)}
            selectedIndex={data.TrustTypeOptions.findIndex(({ value }) => value === field.value)}
            onChange={index => setValue('trustType', data.TrustTypeOptions[index].value)}
            ariaLabel="Trust Type"
            mb="24"
          />
        )}
      />

      {trustType === TrustType.Irrevocable && (
        <>
          <Controller
            control={control}
            name="grantors"
            render={({ field: { value, onChange, disabled } }) => (
              <Select
                isCreatable
                isPortal
                isMulti
                label="Grantors"
                value={value}
                disabled={disabled}
                onChange={opt => {
                  if (opt) {
                    onChange(opt);
                  }
                }}
                options={data.GrantorOptions}
                getOptionValue={getPersonOptionValue}
                mb="24"
                onCreateOption={opt => {
                  if (opt) {
                    onChange([...grantors, { label: opt, value: 2 }]);
                  }
                }}
                placeholder="Select or add Grantors"
                noOptionsMessage={() => 'Please enter a Grantor name'}
                formatCreateLabel={createValue => `Add ${createValue}`}
              />
            )}
          />
        </>
      )}

      <Input {...register('situs')} label="State whose laws will govern this trust" mb="24" />

      <Controller
        control={control}
        name="trustees"
        render={({ field: { value, onChange, ...field } }) => {
          return (
            <Select
              {...field}
              isCreatable
              isPortal
              isMulti
              value={value}
              options={data.TrusteeOption}
              getOptionValue={getPersonOptionValue}
              onChange={opt => {
                if (opt) {
                  onChange(opt);
                }
              }}
              onCreateOption={opt => {
                if (opt) {
                  onChange([...trustees, { label: opt, value: TrusteeType.Other }]);
                }
              }}
              aria-label="Trustees"
              label="Trustees"
              placeholder="Select or add Trustees"
              noOptionsMessage={() => 'Please enter a Trustee name'}
              formatCreateLabel={createValue => `Add ${createValue}`}
              mb="24"
            />
          );
        }}
      />

      {isEstatePlanning && (
        <Controller
          control={control}
          name="includeInGrossEstate"
          render={({ field }) => (
            <Checkbox
              disabled={field.disabled}
              checked={
                isDefaultExcludedGrossEstate
                  ? !!field.value
                  : field.value === null
                  ? false
                  : !field.value
              }
              onChange={evt =>
                field.onChange(
                  isDefaultExcludedGrossEstate ? evt.target.checked : !evt.target.checked,
                )
              }
              mb="24"
            >
              {isDefaultExcludedGrossEstate
                ? 'Include in Gross Estate'
                : 'Exclude from Gross Estate'}
            </Checkbox>
          )}
        />
      )}

      {trustType === TrustType.Irrevocable && (
        <Box display="flex" alignItems="center" mb="24">
          <Checkbox {...register('isGstExempt')}>Generation-Skipping Trust Exempt</Checkbox>
          <GstExemptPopover />
        </Box>
      )}

      <InputMask
        {...register('datedDate')}
        label="Trust Effective Date"
        mask="99/99/9999"
        placeholder="MM/DD/YYYY"
        leftIcon="far fa-calendar-alt"
        formVariant={errors.datedDate ? 'error' : undefined}
        helperText={errors.datedDate?.message}
        css="width: 270px"
      />

      {data.HasSpouse && (
        <Box display="flex" flexDirection="column" style={{ gap: 24 }} mt="24">
          <Button
            variant="link"
            leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
            onClick={() => setShowAdvanced(!showAdvanced)}
            aria-expanded={showAdvanced}
            size="small"
            mr="auto"
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced
          </Button>

          {showAdvanced && (
            <>
              <Box>
                <Text fontWeight={600} mb="8">
                  Liquid Asset Distribution Strategy
                </Text>

                <Controller
                  control={control}
                  name="percentageAtFirstSpouseDeath"
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      label="Disbursements at the death of the first decedent"
                      rightIcon="far fa-percent"
                      decimalScale={2}
                      allowNegative={false}
                      onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                      {...field}
                    />
                  )}
                />
              </Box>

              {/* To be added later
              {illiquidAssetOptions.length > 0 && (
                <Box>
                  <Text fontWeight={600} mb="8">
                    Illiquid Asset Distribution Strategy
                  </Text>

                  <Controller
                    name="assetsAtFirstSpouseDeath"
                    control={control}
                    render={({ field: { ref, onChange, value, ...field } }) => (
                      <Select
                        label="Assets to disburse or transfer at the death of the first decedent"
                        options={illiquidAssetOptions}
                        value={illiquidAssetOptions.filter(opt => value?.includes(opt?.value))}
                        onChange={opts => onChange(opts.map(opt => opt.value))}
                        innerRef={ref}
                        isMulti
                        isPortal
                        {...field}
                      />
                    )}
                  />
                </Box>
              )}
              */}
            </>
          )}
        </Box>
      )}
    </ScrollableContent>
  );
};
